import { JourneyState } from '../components/common/CommonTypes';
/**
 * Shared constants for the client app.
 *
 * Includes:
 * - Route templates (`PageAbsolutePath`, `PageRelativePath`).
 * - Lookup enums/ids for vehicle/product flows.
 * - Journey enums and registration state.
 * - Default/initial journey state (`initialJourneyState`).
 * - Shared error message constants.
 */
import { SelectInputHelper } from '../components/common/SelectInput/SelectInput';
import { blankVehicle } from '../components/testData/journeyHelper';

export const PageAbsolutePath = {
  Renewals: {
    Landing: '/:countrycode?/renewal',
    PersonalDetails: '/:countrycode?/renewal/renewalsjourney/personaldetails',
    VehicleDetails: '/:countrycode?/renewal/renewalsjourney/vehicledetails',
    Quote: '/:countrycode?/renewal/renewalsjourney/quote',
    Summary: '/:countrycode?/renewal/renewalsjourney/summary',
    DirectDebit: '/:countrycode?/renewal/renewalsjourney/directdebit',
    SinglePayment: '/:countrycode?/renewal/renewalsjourney/singlepayment',
    Confirmation: '/:countrycode?/renewal/renewalsjourney/confirmation',
    FailedRegistration:
      '/:countrycode?/renewal/renewalsjourney/failedregistration',
    LateRenewalAcceptance:
      '/:countrycode?/renewal/renewalsjourney/laterenewalacceptance',
    PaymentAssistSuccess:
      '/:countrycode?/renewal/renewalsjourney/paymentassist/success',
    PaymentAssistFailure:
      '/:countrycode?/renewal/renewalsjourney/paymentassist/failure'
  },
  Customer: {
    Landing: '/:countryCode?/customer',
    VehicleDetails: '/:countrycode?/customer/customerjourney/vehicledetails',
    Products: '/:countrycode?/customer/customerjourney/products',
    CreatePlan: '/:countrycode?/customer/customerjourney/createplan',
    PersonalDetails: '/:countrycode?/customer/customerjourney/personaldetails',
    Summary: '/:countrycode?/customer/customerjourney/summary',
    DirectDebit: '/:countrycode?/customer/customerjourney/directdebit',
    SinglePayment: '/:countrycode?/customer/customerjourney/singlepayment',
    Confirmation: '/:countrycode?/customer/customerjourney/confirmation',
    FailedRegistration:
      '/:countrycode?/customer/customerjourney/failedregistration',
    PaymentAssistSuccess:
      '/:countrycode?/customer/customerjourney/paymentassist/success',
    PaymentAssistFailure:
      '/:countrycode?/customer/customerjourney/paymentassist/failure'
  },
  Landing: '/:countrycode?',
  VehicleDetails: '/:countrycode?/dealerjourney/vehicledetails',
  Products: '/:countrycode?/dealerjourney/products',
  CreatePlan: '/:countrycode?/dealerjourney/createplan',
  PersonalDetails: '/:countrycode?/dealerjourney/personaldetails',
  Summary: '/:countrycode?/dealerjourney/summary',
  DirectDebit: '/:countrycode?/dealerjourney/directdebit',
  SinglePayment: '/:countrycode?/dealerjourney/singlepayment',
  Confirmation: '/:countrycode?/dealerjourney/confirmation',
  FailedRegistration: '/:countrycode?/dealerjourney/failedregistration',
  UnderMaintenance: '/:countrycode?/undermaintenance',
  PaymentAssistSuccess: '/:countrycode?/dealerjourney/paymentassist/success',
  PaymentAssistFailure: '/:countrycode?/dealerjourney/paymentassist/failure',
  Error: '/:countrycode?/errorpage',
  Renewal: '/:countrycode?/renewal',
  Suppression: '/:countrycode?/suppression',
  Withdrawal: '/:countrycode?/withdrawal'
} as const;

export const PageRelativePath = {
  PersonalDetails: '../personaldetails',
  VehicleDetails: '../vehicledetails',
  Quote: '../quote',
  Products: '../products',
  CreatePlan: '../createplan',
  Summary: '../summary',
  DirectDebit: '../directdebit',
  SinglePayment: '../singlepayment',
  PaymentAssistSuccess: '../paymentassist/success',
  PaymentAssistFailure: '../paymentassist/failure',
  Confirmation: '../confirmation',
  CustomerFailedRegistration: '../customerjourney/failedregistration', //as this is navigated from the manager it needs the journey specifying
  DealerFailedRegistration: '../dealerjourney/failedregistration', //as this is navigated from the manager it needs the journey specifying
  RenewalFailedRegistration: '../renewalsjourney/failedregistration', //as this is navigated from the manager it needs the journey specifying
  LateRenewalAcceptance: '../laterenewalacceptance'
} as const;

export const VehicleLookup = {
  RegistrationDate: 0,
  Types: 1,
  Makes: 2,
  Model: 3,
  Fueltype: 4,
  Enginesize: 5,
  Modeltrim: 6,
  Finished: 7
};

export const ProductTypes = {
  Alloy: 36,
  AssistancePlus: 41,
  Cosmetic: 25,
  Gap: 26,
  MOT: 27,
  RoadsideAssistance: 30,
  ServicePlan: 31,
  Tyre: 32,
  Warranty: 33
} as const;

export const RegistrationState = {
  Initial: 0,
  Registering: 1,
  Success: 2,
  Failed: 3,
  VoidingPayment: 4
} as const;

export const ProductChannel = {
  Customer: 2,
  DealerJourney: 3,
  OpenSite: 4,
  RenewalJourney: 5
} as const;

export const PaymentDay = {
  Default: '31',
  Empty: '0'
} as const;

export const Journey = {
  Initial: -1,
  General: 0,
  DealerJourney: 1,
  Renewals: 2,
  CallCentre: 3,
  Customer: 4
} as const;

export enum VehicleIdType {
  Vrn = 1,
  Vin,
  Email
}

export const VehicleSource = {
  System: 0,
  User: 1
} as const;

export const LateRenewalConditions = [
  'The vehicle for which this warranty is being purchased has no faults.',
  'If the vehicle is later found to have faults that were present at time of purchase the claim could be voided, and no payment made.',
  'I agree to provide documentation in respect of ownership and insurance if requested as part of any claims validation.',
  'I am not a director or majority shareholder in any motor trade business involved in the repair and maintenance or sale of motor vehicles.',
  'I am not using the vehicle for hire and reward purposes (for example as use as a taxi or delivery vehicle).',
  'The vehicle has been serviced in line with the manufacturer recommended schedule.'
];

export const LateRenewalCallCenterConditions = [
  'The vehicle does not have any pre-existing faults or pending claims.',
  'Customer is aware any faults that occurred before this policy started will not be covered under this policy. If the vehicle is later found to have faults that were present at the purchase of this policy the claim could be voided and no payment will be made.'
];

export const RenewalSourceSystem = {
  None: 0,
  ODIE: 1,
  VSC: 2,
  API: 3
} as const;

export const VehicleAPIErrorMessages = {
  VregError:
    "Sorry we couldn't find a vehicle matching that registration. Please try again or click 'Enter details manually' below to select your vehicle manually.",
  VinError:
    "Sorry we couldn't find a vehicle matching that VIN. Please try again or click 'Enter details manually' below to select your vehicle manually."
} as const;

export const QuoteAPIErrorMessages = {
  VregError:
    "Sorry we couldn't find a vehicle matching that registration. Please try again or click 'Enter details manually' below to select your vehicle manually.",
  VinError:
    'Sorry we could not find a vehicle matching that VIN. Please try again, enter the vehicle registration or enter the details manually.',
  EmailError:
    'Sorry we could not find a vehicle matching from that email address. Please try again, enter the vehicle registration or enter the details manually.'
} as const;

export const RenewalAPIErrorMessages = {
  VregError:
    "Sorry we couldn't find the full details of the vehicle matching that registration. Please contact our support team on 0344 573 8002.",
  RecordIdError:
    "Sorry we couldn't find the full details of vehicle matching the registration. Please enter the vehicle details manually."
} as const;

export const initialJourneyState: JourneyState = {
  vehicle: {
    vehicleDetails: blankVehicle,
    vehicleDeliveryDate: undefined,
    currentMileage: null,
    vehiclePurchasePrice: 0,
    salesId: '',
    dealerReference: '',
    deliveryDateEstimated: false,
    vehicleSource: VehicleSource.System
  },
  products: [],
  documents: [],
  personalDetail: {
    title: SelectInputHelper.NullValue(),
    firstName: '',
    surname: '',
    dateOfBirth: undefined,
    email: '',
    confirmEmail: '',
    phone: '',
    addressDetails: {
      addressLine1: '',
      addressLine2: '',
      townOrCity: '',
      region: '',
      country: '',
      postCode: ''
    }
  },
  directDebitDetails: {
    accountHolderName: '',
    sortCode1: null,
    sortCode2: null,
    sortCode3: null,
    accountNumber: null,
    selectedPaymentDay: PaymentDay.Default,
    bankName: ''
  },
  agreedTermsAndConditions: false,
  renewalMarketing: false,
  navigationStatus: {
    vehicle: false,
    products: false,
    personalDetails: false,
    checkout: false,
    confirmation: false,
    quote: false
  },
  singlePaymentDetails: {
    billingAddress: {
      addressLine1: '',
      addressLine2: '',
      townOrCity: '',
      region: '',
      country: '',
      postCode: ''
    }
  },
  registrationFailMsg: '',
  registrationState: RegistrationState.Initial,
  orderNumbers: [],
  expandedDocIds: [],
  foundExistingQuotes: false,
  dealerCode: '',
  token: '',
  tokenExpiresOn: new Date(),
  policyStartDate: new Date(),
  isLateRenewal: false,
  promoCode: '',
  bankAuthorisationCode: '',
  quoteId: 0,
  hasQuoteExpired: false,
  quoteProducts: [],
  isExpiredMessageAcknowledged: false
};

export const functionNotImplemented = (): void => {
  console.error('onPolicyStartDateChange not implemented');
};

export const CaptchaMessages = {
  ValidationMessage: 'Captcha validation failed, try again.'
};

export const QuoteType = {
  Customer: 1,
  Dealer: 2
} as const;

export enum SagePayAccount {
  Default = '',
  CallCentre = '/cc'
}

export const DefaultRadioOptions = ['Yes', 'No'];
